import { useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Card } from '@/src/elements/card'
import { Textarea } from '@/src/elements/input'
import { Modal } from '@/src/elements/modal/Modal'
import { Typography } from '@/src/elements/text/Typography'
import { useCreateUpliftMutation } from '@/src/features/intention/services/useCreateUpliftMutation'
import { getWatchmanDailyIntentionsOptions } from '@/src/features/watchman/services/useWatchmanDailyIntentionsQuery'
import { useToast } from '@/src/general/components/ToastContext'
import { TEST_ID } from '@/src/general/constants/testId'
import { ErrorScreen } from '@/src/general/ui/screens/ErrorScreen'
import { parseApiError } from '@/src/general/utilities/fetcher/parseApiError'
import { queryClient } from '@/src/general/utilities/query'
import { useTranslate } from '@/src/lang/useTranslate'

type TCreateUpliftModalProps = {
  showModal: boolean
  onClose: VoidFunction
  onConfirm: VoidFunction
  intention?: string
  userName: string
  intentionId: number
  prayerId: number
}

export const CreateUpliftModal = ({
  onClose,
  onConfirm,
  showModal,
  intention,
  userName,
  intentionId,
  prayerId,
}: TCreateUpliftModalProps): ReactNode => {
  const [upliftMessage, setUpliftMessage] = useState<string>('')
  const { t } = useTranslate()
  const { addToast } = useToast()

  const invalidateWatchmanDailyIntentions = () => {
    queryClient.invalidateQueries(getWatchmanDailyIntentionsOptions({ enabled: true }))
  }
  const { mutateAsync } = useCreateUpliftMutation({
    intentionId,
    prayerId,
    onSuccess: invalidateWatchmanDailyIntentions,
  })

  const handleSubmit = async () => {
    if (!upliftMessage.length) return
    try {
      await mutateAsync(upliftMessage)
      addToast({
        message: t('pray.uplift.sent'),
        colors: 'success',
      })
      onConfirm()
    } catch (error) {
      addToast({
        message: t(parseApiError(error) || 'uplift.message.error'),
        colors: 'danger',
      })
    }
  }

  if (!intention || !prayerId) return <ErrorScreen defaultErrorKey="missing.parameters" />

  return (
    <Modal
      title={<FormattedMessage id="uplift.message" />}
      onCancel={onClose}
      onConfirm={handleSubmit}
      showModal={showModal}
      showCancelButton
      confirmText={t('word.send')}
      confirmButtonProps={{ isDisabled: upliftMessage.length === 0 }}
    >
      <View className="flex w-full flex-col gap-6 py-2">
        <Card className="rounded-xl bg-default-bg-light p-4">
          <Typography bold>{userName}</Typography>
          <Typography>{intention}</Typography>
        </Card>
        <View className="flex w-full flex-col gap-1">
          <Textarea
            inBottomSheet
            testID={TEST_ID.INTENTION.UPLIFT.CREATE.TEXTAREA}
            color="default"
            value={upliftMessage}
            placeholder={t('uplift.textarea.placeholder.short')}
            onChangeText={setUpliftMessage}
            numberOfLines={3}
          />
        </View>
      </View>
    </Modal>
  )
}
